import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { WidgetError } from "@/components/WidgetError";
import { toast } from "sonner";

const CATEGORIES = [
  "parking", "washbay", "shower", "diesel_offload", "diesel_load",
  "paraffin", "molasses", "refuel", "custom"
];

export default function ServiceTypes() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", label: "", category: "custom", unit: "unit" });
  const [stepsRaw, setStepsRaw] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/service-types", { params: { limit: 100 } });
      setItems(data.items);
    } catch (e) {
      setError(e);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      const steps = stepsRaw.split("\n").map((line, i) => {
        const s = line.trim();
        if (!s) return null;
        return { key: s.toLowerCase().replace(/[^a-z0-9]+/g, "_"), label: s, required: true, order: i };
      }).filter(Boolean);
      await api.post("/service-types", { ...form, workflow_steps: steps });
      toast.success("Service type created");
      setOpen(false); setForm({ code: "", label: "", category: "custom", unit: "unit" }); setStepsRaw("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggle = async (t) => {
    try {
      await api.patch(`/service-types/${t.id}`, { is_active: !t.is_active });
      load();
    } catch (e) { toast.error("Failed"); }
  };
  const archive = async (t) => {
    if (!window.confirm(`Archive ${t.label}?`)) return;
    try {
      await api.post(`/service-types/${t.id}/archive`);
      toast.success("Archived");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="label-overline">Management</div>
          <h1 className="text-3xl font-black tracking-tight mt-1">Service Types</h1>
          <p className="text-slate-600 mt-1 text-sm">Enable, disable, or create custom order types.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="svc-new" className="rounded bg-blue-700 hover:bg-blue-700">+ New service type</Button>
          </DialogTrigger>
          <DialogContent className="rounded">
            <DialogHeader><DialogTitle>New service type</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-overline">Code</Label>
                  <Input data-testid="svc-code" className="rounded h-10 mt-1 font-mono"
                         value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toLowerCase() })}
                         placeholder="e.g. diesel_offload" />
                </div>
                <div>
                  <Label className="label-overline">Label</Label>
                  <Input data-testid="svc-label" className="rounded h-10 mt-1"
                         value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
                </div>
                <div>
                  <Label className="label-overline">Category</Label>
                  <select data-testid="svc-category"
                          className="w-full h-10 mt-1 border border-slate-300 rounded px-2 font-mono"
                          value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="label-overline">Unit</Label>
                  <select data-testid="svc-unit"
                          className="w-full h-10 mt-1 border border-slate-300 rounded px-2 font-mono"
                          value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}>
                    <option value="unit">unit</option>
                    <option value="hour">hour</option>
                    <option value="litre">litre</option>
                    <option value="flat">flat</option>
                  </select>
                </div>
              </div>
              <div>
                <Label className="label-overline">Workflow steps (one per line)</Label>
                <textarea data-testid="svc-steps"
                          className="w-full mt-1 border border-slate-300 rounded p-2 h-24 font-mono text-sm"
                          value={stepsRaw} onChange={(e) => setStepsRaw(e.target.value)}
                          placeholder={"Arrival check\nWash started\nWash completed"} />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-2">
              <Button variant="outline" className="rounded" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="svc-create" className="rounded bg-blue-700 hover:bg-blue-700"
                      onClick={create} disabled={!form.code || !form.label}>Create</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-slate-200">
        {error ? (
          <div className="p-4">
            <WidgetError
              error={error}
              onRetry={load}
              title="Could not load service types"
              testId="service-types-error"
            />
          </div>
        ) : (
        <table className="tbl w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="label-overline text-slate-600">
              <th className="text-left px-4 py-3">Code</th>
              <th className="text-left px-4 py-3">Label</th>
              <th className="text-left px-4 py-3">Category</th>
              <th className="text-left px-4 py-3">Unit</th>
              <th className="text-left px-4 py-3">Active</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">Loading…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">
                No service types yet — create your first one to enable "New Truck Entry".
              </td></tr>
            )}
            {items.map((t) => (
              <tr key={t.id} className="border-b border-slate-100">
                <td className="px-4 py-3 font-mono">{t.code}</td>
                <td className="px-4 py-3 font-semibold">{t.label}</td>
                <td className="px-4 py-3 font-mono">{t.category}</td>
                <td className="px-4 py-3 font-mono">{t.unit}</td>
                <td className="px-4 py-3">
                  <button onClick={() => toggle(t)}
                          data-testid={`svc-toggle-${t.code}`}
                          className={`pill ${t.is_active ? "pill-active" : "pill-cancelled"}`}>
                    {t.is_active ? "active" : "disabled"}
                  </button>
                </td>
                <td className="px-4 py-3">
                  <button onClick={() => archive(t)}
                          data-testid={`svc-archive-${t.code}`}
                          className="text-red-600 hover:underline text-sm">Archive</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )}
      </div>
    </div>
  );
}
